import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import pdfMake from 'pdfmake'
import Div from '@jumbo/shared/Div'
import { Pagination, Stack } from '@mui/material'
import Typography from '@mui/material/Typography'
import { LoadingButton } from '@mui/lab'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import DownloadIcon from '@mui/icons-material/Download'
import CircularProgress from '@mui/material/CircularProgress'
import { useSnackbar } from 'notistack'
import vfs from '../../../../../../../fonts/NimbusSanL/nimbus-san'
import MedicalInformationReportFilter from './MedicalInformationReportFilter'
import { buildCsv, downloadCsv } from 'app/utils/reports/csvExport'
import '../../../../../../pages/reports/reportstyles.css'

const severityLabels = ['Normal', 'Medium', 'High', 'Critical']

/** pdfmake landscape table column widths — keep in sync with `headers.length`. */
const medicalInformationPdfTableWidths = [100, 85, 95, 95, 135, 60, '*', 80, 90]

const MedicalInformationReportLayout = ({
    imageData,
    viewMutation,
    pdfMutation,
    pageMutation,
    apiCallInProgress,
    setApiCallInProgress,
    pdfData,
}) => {
    const { medicalInformationReport, loading, error } = useSelector((state) => state.reports)
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const [isFilterVisible, setFilterVisible] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [pdfDownloading, setPdfDownloading] = useState(false)
    const [pdfTemplate, setpdfTemplate] = useState({})
    const itemsPerPage = 25

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    const handleError = useCallback(
        (err) => {
            if (err.status === '401') {
                navigate('/profile/signout')
            } else if (err.detail) {
                enqueueSnackbar(err.detail, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            } else {
                enqueueSnackbar(err, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            }
        },
        [enqueueSnackbar, navigate]
    )

    pdfMake.vfs = vfs
    pdfMake.fonts = {
        NimbusSans: {
            normal: 'NimbusSanL-Reg.otf',
            bold: 'NimbusSanL-Bol.otf',
            italics: 'NimbusSanL-RegIta.otf',
            bolditalics: 'NimbusSanL-BolIta.otf',
        },
    }

    const handlePageChange = async (page) => {
        try {
            await pageMutation.mutate(page)
            setCurrentPage(page)
        } catch (e) {
            console.error('Error updating page:', e)
        }
    }

    const handleFilterClick = () => {
        setFilterVisible(!isFilterVisible)
    }

    const headers = React.useMemo(
        () => [
            'Customer',
            'Phone',
            'Participant',
            'Family',
            'Conditions',
            'Max severity',
            'Medical comments',
            'Emergency contact name',
            'Emergency contact phone',
        ],
        []
    )

    const parseToPdfData = useMemo(
        () => (data) => {
            return data.map((d) => {
                const customerName = d ? `${d.customerFirstName ?? ''} ${d.customerLastName ?? ''}`.trim() : ''
                const participantName = d ? `${d.memberFirstName ?? ''} ${d.memberLastName ?? ''}`.trim() : ''
                const maxSev =
                    d?.maxSeverity === null || d?.maxSeverity === undefined
                        ? ''
                        : severityLabels[d.maxSeverity] ?? String(d.maxSeverity)
                return [
                    { text: customerName, style: 'leftAlign' },
                    { text: d?.customerPhoneNumber ?? '', style: 'leftAlign' },
                    { text: participantName, style: 'leftAlign' },
                    { text: d?.familyName ?? '', style: 'leftAlign' },
                    { text: d?.medicalConditionsSummary ?? '', style: 'leftAlign' },
                    { text: maxSev, style: 'cell' },
                    { text: d?.medicalComments ?? '', style: 'leftAlign' },
                    { text: d?.medicalEmergencyContactName ?? '', style: 'leftAlign' },
                    { text: d?.medicalEmergencyContactPhone ?? '', style: 'leftAlign' },
                ]
            })
        },
        []
    )

    const pageRow = []

    const tableBody =
        pdfData?.data?.length > 0
            ? parseToPdfData(pdfData.data).map((row) =>
                  row.map((cell, index) => ({
                      text: cell,
                      style: ['cell', index === 5 ? 'center' : 'leftAlign'],
                  }))
              )
            : []

    const pdfTemplateDefault = React.useMemo(() => {
        return {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [10, 45, 15, 45],
            footer: function (currentPage, pageCount) {
                const isLastPage = currentPage === pageCount
                if (!isLastPage) {
                    return {
                        columns: [
                            {
                                table: {
                                    headerRows: 0,
                                    widths: medicalInformationPdfTableWidths,
                                    body: [pageRow.map((cell) => ({ text: cell.text, style: cell.style }))],
                                },
                                margin: [15, 10, 15, 10],
                            },
                        ],
                    }
                }

                return {
                    columns: [
                        {
                            table: {
                                headerRows: 0,
                                widths: medicalInformationPdfTableWidths,
                                body: [pageRow.map((cell) => ({ text: cell.text, style: cell.style }))],
                            },
                            margin: [15, 5, 15, 20],
                        },
                    ],
                }
            },
            content: [
                {
                    image: imageData,
                    width: 70,
                },
                {
                    text: 'Medical Information Report',
                    style: 'header',
                    alignment: 'center',
                },
                {
                    layout: {
                        fillColor: function (rowIndex) {
                            return rowIndex % 2 === 1 ? '#f2f2f2' : null
                        },
                    },
                    style: 'withMargin',
                    alignment: 'center',
                    table: {
                        headerRows: 1,
                        widths: medicalInformationPdfTableWidths,
                        body: [
                            headers.map((header) => {
                                return { text: header, style: 'headerRow' }
                            }),
                            ...tableBody,
                        ],
                    },
                },
            ],
            defaultStyle: {
                font: 'NimbusSans',
            },
            styles: {
                withMargin: {
                    margin: [5, 20, 20, 5],
                },
                header: {
                    fontSize: 18,
                    bold: true,
                },
                headerRow: {
                    fillColor: '#ADD8E6',
                    bold: true,
                    fontSize: 10,
                },
                cell: {
                    fontSize: 9,
                    alignment: 'center',
                },
                leftAlign: {
                    alignment: 'left',
                    fontSize: 9,
                },
            },
        }
    }, [imageData, headers, pdfData])

    useEffect(() => {
        const template = { ...pdfTemplateDefault }
        setpdfTemplate(template)
    }, [headers, pdfData, pdfTemplateDefault])

    const create = async () => {
        await pdfMutation.mutate()
        setPdfDownloading(true)
    }

    const createCsv = async () => {
        try {
            const data = await pdfMutation.mutateAsync()
            const pdfRows = data?.data?.length ? parseToPdfData(data.data) : []
            const rows = pdfRows.map((row) =>
                row.map((cell) => (typeof cell === 'object' && cell?.text != null ? cell.text : cell))
            )
            const csvString = buildCsv(headers, rows)
            const today = new Date()
            downloadCsv(
                csvString,
                `Medical_Information_Report_${today.toLocaleDateString()}_${today.toLocaleTimeString()}.csv`
            )
        } catch (err) {
            if (err?.message) handleError(err)
        }
    }

    useEffect(() => {
        if (!pdfDownloading) return
        setPdfDownloading(false)
        const pdfDocGenerator = pdfMake.createPdf(pdfTemplate)
        let today = new Date()
        pdfDocGenerator.download(`Medical_Information_Report_${today.toLocaleDateString()}_${today.toLocaleTimeString()}`)
    }, [pdfTemplate])

    const formatMaxSeverity = (v) => {
        if (v === null || v === undefined) return ''
        return severityLabels[v] ?? String(v)
    }

    return (
        <Div>
            <Div>
                <h1 className='report-heading'>Medical Information Report</h1>
                <Div
                    className='report-loading-button'
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1,
                        marginTop: '-55px',
                        flexWrap: 'wrap',
                    }}>
                    <LoadingButton
                        onClick={create}
                        size={'small'}
                        type='submit'
                        variant={'contained'}
                        disabled={pdfMutation.isPending}>
                        <DownloadIcon sx={{ fontSize: 'medium', marginRight: '2px' }} /> Download Report
                    </LoadingButton>
                    <LoadingButton
                        onClick={createCsv}
                        size={'small'}
                        type='button'
                        variant={'contained'}
                        disabled={pdfMutation.isPending}>
                        <DownloadIcon sx={{ fontSize: 'medium', marginRight: '2px' }} /> Download CSV
                    </LoadingButton>
                    <LoadingButton onClick={handleFilterClick} size={'small'} type='submit' variant={'contained'}>
                        <FilterAltIcon fontSize='medium' />
                    </LoadingButton>
                </Div>
            </Div>
            {isFilterVisible && <MedicalInformationReportFilter mutation={viewMutation} />}
            <>
                <div className='report-container'>
                    {loading && (
                        <CircularProgress
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                            }}
                        />
                    )}
                    {!loading && medicalInformationReport.totalCount === 0 ? (
                        <Typography variant={'primary'}>No data. Select suitable filter to view data</Typography>
                    ) : (
                        <div className='custom-table'>
                            <table>
                                <tbody>
                                    <tr>
                                        {headers.map((h) => (
                                            <th key={h}>{h}</th>
                                        ))}
                                    </tr>
                                </tbody>
                                <tbody>
                                    {medicalInformationReport.data?.map((d, index) => {
                                        const customerName = d
                                            ? `${d.customerFirstName ?? ''} ${d.customerLastName ?? ''}`.trim()
                                            : ''
                                        const participantName = d
                                            ? `${d.memberFirstName ?? ''} ${d.memberLastName ?? ''}`.trim()
                                            : ''

                                        return (
                                            <tr key={index}>
                                                <td style={{ textAlign: 'left', width: '140px' }}>{customerName}</td>
                                                <td style={{ textAlign: 'left', width: '120px' }}>
                                                    {d?.customerPhoneNumber ?? ''}
                                                </td>
                                                <td style={{ textAlign: 'left', width: '140px' }}>
                                                    {participantName}
                                                </td>
                                                <td style={{ textAlign: 'left', width: '120px' }}>{d?.familyName}</td>
                                                <td style={{ textAlign: 'left', minWidth: '200px' }}>
                                                    {d?.medicalConditionsSummary}
                                                </td>
                                                <td style={{ textAlign: 'center', width: '100px' }}>
                                                    {formatMaxSeverity(d?.maxSeverity)}
                                                </td>
                                                <td style={{ textAlign: 'left', minWidth: '160px' }}>
                                                    {d?.medicalComments}
                                                </td>
                                                <td style={{ textAlign: 'left', minWidth: '120px' }}>
                                                    {d?.medicalEmergencyContactName ?? ''}
                                                </td>
                                                <td style={{ textAlign: 'left', width: '120px' }}>
                                                    {d?.medicalEmergencyContactPhone ?? ''}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    <tr>
                                        <td colSpan={headers.length} />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Stack>
                        <Pagination
                            count={Math.ceil(medicalInformationReport.totalCount / itemsPerPage)}
                            onChange={(event, page) => {
                                handlePageChange(page)
                            }}
                            page={currentPage}
                            siblingCount={1}
                            boundaryCount={1}
                            sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px', marginBottom: '5px' }}
                            variant='outlined'
                        />
                    </Stack>
                </div>
            </>
        </Div>
    )
}

export default MedicalInformationReportLayout
